import { z } from "zod";
import { MIX_BUCKETS } from "./contentMix.js";
import { POST_ROLES } from "./structure.js";
// =============================================================================
// Validation for campaigns and monthly plans (spec §11).
// =============================================================================
const dayKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
export const structureStepSchema = z.object({
    role: z.enum(POST_ROLES),
    at: z.number().min(0).max(1),
    guidance: z.string().min(1).max(500),
    repeatable: z.boolean().optional(),
});
/**
 * Cross-field checks on a campaign window.
 *
 * Shared by create AND preview, deliberately. The preview is what the client
 * reads before committing (spec §6, §9), so it must reject everything create
 * would — otherwise the preview happily renders a nonsense window and the
 * refusal only arrives after they have agreed to it.
 */
function refineCampaignWindow(input, ctx) {
    if (input.endDate < input.startDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["endDate"],
            message: "The end date must be on or after the start date",
        });
        // No point reporting the density too: the span is meaningless until the
        // dates are the right way round.
        return;
    }
    // A campaign needs room to breathe: the structure's pacing is the point, and
    // five posts crammed into two days is not a campaign.
    const days = daySpan(input.startDate, input.endDate);
    if (input.postCount > days * 2) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["postCount"],
            message: `${input.postCount} posts across ${days} day(s) is more than two a day. ` +
                `Lengthen the campaign or ask for fewer posts.`,
        });
    }
}
/**
 * A campaign the client is starting.
 *
 * `templateKey` is nullable because spec §6 allows a custom campaign from just
 * a goal and dates.
 */
export const createCampaignSchema = z
    .object({
    brandId: z.string().uuid(),
    templateKey: z.string().max(80).nullish(),
    name: z.string().min(2).max(120),
    startDate: dayKey,
    endDate: dayKey,
    postCount: z.number().int().min(1).max(60),
    /** Pages to publish to; empty means every active page on the brand. */
    socialAccountIds: z.array(z.string().uuid()).max(20).default([]),
    /** The client's offer details — "20% off all shoes". */
    notes: z.string().max(2000).optional(),
})
    .superRefine(refineCampaignWindow);
function daySpan(from, to) {
    const a = Date.parse(`${from}T00:00:00Z`);
    const b = Date.parse(`${to}T00:00:00Z`);
    if (Number.isNaN(a) || Number.isNaN(b))
        return 1;
    return Math.max(1, Math.round((b - a) / 86_400_000) + 1);
}
export const contentMixSchema = z.object(Object.fromEntries(MIX_BUCKETS.map((b) => [b, z.number().min(0).max(100)])));
export const createMonthlyPlanSchema = z.object({
    brandId: z.string().uuid(),
    /** First day of the month being planned. */
    month: dayKey,
    postCount: z.number().int().min(1).max(100),
    contentMix: contentMixSchema,
    /** Campaigns already in that month to leave room for. */
    includeCampaignIds: z.array(z.string().uuid()).max(20).default([]),
    /** Empty = every connected page, as it always was. */
    socialAccountIds: z.array(z.string().uuid()).max(20).default([]),
});
/**
 * Preview request. Separate from creation on purpose: spec §6 flow 5 shows a
 * calendar preview BEFORE confirming, and §9 requires showing how many posts a
 * plan will consume before it runs.
 */
export const previewPlanSchema = z.object({
    brandId: z.string().uuid(),
    month: dayKey,
    postCount: z.number().int().min(1).max(100),
    contentMix: contentMixSchema,
    /**
     * Must match createMonthlyPlanSchema.
     *
     * The preview reports how many posts the month will consume, and that count
     * is per page. Leaving this off the preview meant it counted every page
     * while create counted the chosen ones — the owner would be shown one
     * number and billed against another.
     */
    socialAccountIds: z.array(z.string().uuid()).max(20).default([]),
});
/** Preview for a campaign, before any generation spend. */
export const previewCampaignSchema = z
    .object({
    brandId: z.string().uuid(),
    templateKey: z.string().max(80).nullish(),
    startDate: dayKey,
    endDate: dayKey,
    postCount: z.number().int().min(1).max(60),
    socialAccountIds: z.array(z.string().uuid()).max(20).default([]),
})
    // Same window rules as create — see refineCampaignWindow.
    .superRefine(refineCampaignWindow);
//# sourceMappingURL=schema.js.map